import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Buffer } from 'buffer';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import FormItem from 'components/form/FormItem';
import Loading from 'components/global/Loading';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import {
	getPrintSettings,
	setIsUserSelectedLocale,
	setPrintLocale,
	setPrintSettings
} from 'pages/Settings/Print/PrintSettingsSlice';
import usePrintUtils from 'pages/Settings/Print/hooks/usePrintUtils';
import useQrCode from 'pages/Settings/Print/hooks/useQrCode';
import useSauidiQR from 'pages/Settings/Print/hooks/useSauidiQR';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import { languages } from 'utils/localization';

import usePrintLocale from './usePrintLocale';

const PrintOrdersWithAWB = NiceModal.create(({ ids, clientData, defaultLang }) => {
	// States
	const [awbData, setAWBData] = useState(null);
	const [orderData, setOrderData] = useState(null);
	const [error, setError] = useState(null);
	const [internationalOrders, setInternationalOrders] = useState([]);
	const [successfulIDs, setSuccessfulIDs] = useState([]);

	// Print States
	const [invoicePrintSettings, setInvoicePrintSettings] = useState(null);
	const [awbPrintSettings, setAWBPrintSettings] = useState(null);

	// Hooks
	const { showError } = useAlert();
	const modal = useModal();
	const { generateQrCode } = useQrCode();
	const { generateBase64String } = useSauidiQR();
	const dispatch = useDispatch();
	const isAuthorized = useAccessControl();
	const { printLocale } = useSelector((state) => state?.printSettings);
	const { fetchLanguage } = usePrintLocale();
	const {
		fetchAWBData,
		fetchOrderPrintData,
		handlePDFDownload,
		updatePrintStatus,
		isLoading,
		pdfFile,
		mergePDF,
		renderPDFChunks,
		renderPDF
	} = usePrintUtils();
	const { t } = useTranslation();
	const { Country } = useLocale();

	const companySettings = useSelector((state) => state?.user?.generalSettings) ?? null;
	const { mobile = false } = useSelector((state) => state?.layout);
	const isMobileDevice = useMediaQuery(Breakpoints.mobile);

	// static
	const langs = useMemo(() => languages.map((language) => language.code), []);
	const defaultColSpan = {
		md: 2
	};

	// Helpers
	const getInvoicePropJSON = useCallback(async () => {
		const printSettings = await dispatch(getPrintSettings()).unwrap();
		const invoicePrintPropJSON = printSettings?.invoicePrintPropJSON;

		if (!invoicePrintPropJSON) {
			if (window.ReactNativeWebView) {
				window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'error', description: 'noData' }));
			}
			return AppLogger.error('invoicePrintPropJSON not found');
		}

		if (defaultLang) {
			dispatch(setPrintLocale(defaultLang));
			setInvoicePrintSettings({ ...invoicePrintPropJSON, ...{ language: defaultLang } });
		} else {
			setInvoicePrintSettings({ ...invoicePrintPropJSON, ...(printLocale && { language: printLocale }) });
		}
	}, [printLocale, defaultLang]);

	const getAWBPropJSON = useCallback(async () => {
		const printSettings = await dispatch(getPrintSettings()).unwrap();

		const awbPrintPropJSON = printSettings?.awbPrintPropJSON;

		if (!awbPrintPropJSON) {
			if (window.ReactNativeWebView) {
				window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'error', description: 'noData' }));
			}
			return AppLogger.error('awbPrintPropJSON not found');
		}

		if (defaultLang) {
			dispatch(setPrintLocale(defaultLang));
			setAWBPrintSettings({ ...awbPrintPropJSON, ...{ language: defaultLang } });
		} else {
			setAWBPrintSettings({
				...awbPrintPropJSON,
				...(printLocale && { language: printLocale })
			});
		}
	}, [printLocale, defaultLang]);

	const getPrintAWBNAData = useCallback(
		async ({ items }) => {
			const result = [];

			for (const item of items) {
				let saudiQRData;

				if (window.ReactNativeWebView) {
					if (!clientData) {
						window.ReactNativeWebView.postMessage(
							JSON.stringify({ type: 'error', description: 'clientDataNotFound' })
						);
						return AppLogger.error('generateQRDataArr : clientData not found external print');
					}
					saudiQRData = generateBase64String(item.invoiceData, clientData);
				} else {
					saudiQRData = generateBase64String(item.invoiceData, {
						ClientName: companySettings?.ClientName ?? '',
						VATNumber: companySettings?.VATNumber ?? '000000000000000'
					});
				}

				const qrCode = await generateQrCode(saudiQRData, 200);
				result.push({
					propJSON: invoicePrintSettings,
					companyLogo: item.invoiceData?.CompanyLogo ?? null,
					fieldName: 'invoicePrintPropJSON',
					qrCode,
					...item.invoiceData,
					key: item?.invoiceData?.orderID
				});

				const ID = item?.id;
				const itemList = item.invoiceData?.items ? [...item.invoiceData.items] : [];
				const splitItemList = [];

				if (awbPrintSettings?.showOrderItems) {
					while (itemList.length > 0) {
						splitItemList.push(itemList.splice(0, 4));
					}
				}

				if (item?.invoiceData?.boxes?.length > 1 || item?.invoiceData?.packageCount > 1) {
					const { boxes, packageCount } = item.invoiceData;

					const boxCount = Math.max(boxes?.length ?? 0, packageCount ?? 0);

					for (let i = 0; i < boxCount; i += 1) {
						if (!splitItemList.length) {
							result.push({
								propJSON: awbPrintSettings,
								boxIndex: i,
								boxCount,
								companyLogo: item.invoiceData?.CompanyLogo ?? null,
								isAuthorizedOMS: isAuthorized('OMSModule'),
								fieldName: 'awbPrintPropJSON',
								countryISO: Country,
								successMessage: item?.successMessage ?? null,
								...item.invoiceData,
								items: [],
								key: `${item.invoiceData?.orderID}-${i}-${ID}`
							});
							setSuccessfulIDs((prevState) => [...prevState, item.id]);
						} else {
							for await (const [splitIndex, items] of splitItemList.entries()) {
								result.push({
									propJSON: awbPrintSettings,
									boxIndex: i,
									boxCount,
									companyLogo: item.invoiceData?.CompanyLogo ?? null,
									countryISO: Country,
									isAuthorizedOMS: isAuthorized('OMSModule'),
									fieldName: 'awbPrintPropJSON',
									successMessage: item?.successMessage ?? null,
									...item.invoiceData,
									items,
									key: `${item.invoiceData?.orderID}-${splitIndex}-${ID}`
								});
								setSuccessfulIDs((prevState) => [...prevState, item.id]);
							}
						}
					}
				} else {
					if (!splitItemList.length) {
						result.push({
							propJSON: awbPrintSettings,
							boxIndex: 0,
							boxCount: 1,
							companyLogo: item.invoiceData?.CompanyLogo ?? null,
							countryISO: Country,
							isAuthorizedOMS: isAuthorized('OMSModule'),
							fieldName: 'awbPrintPropJSON',
							successMessage: item?.successMessage ?? null,
							...item.invoiceData,
							items: [],
							key: `${item.invoiceData?.orderID}-${ID}`
						});
						setSuccessfulIDs((prevState) => [...prevState, ID]);
					} else {
						for await (const [splitIndex, items] of splitItemList.entries()) {
							result.push({
								propJSON: awbPrintSettings,
								boxIndex: 0,
								boxCount: 1,
								companyLogo: item.invoiceData?.CompanyLogo ?? null,
								countryISO: Country,
								fieldName: 'awbPrintPropJSON',
								successMessage: item?.successMessage ?? null,
								...item.invoiceData,
								items,
								key: `${item.invoiceData?.orderID}-${splitIndex}-${ID}`
							});
							setSuccessfulIDs((prevState) => [...prevState, item.id]);
						}
					}
				}
			}
			return result;
		},
		[awbPrintSettings]
	);

	const renderAwbPDF = useCallback(async (data) => {
		if (data.filePath) {
			try {
				const response = await fetch(data.filePath);
				const pdfBlob = await response.blob();
				return pdfBlob;
			} catch (error) {
				AppLogger.error('Error fetching file with path', error);
				return null;
			}
		} else {
			return renderPDF(data);
		}
	}, []);

	const createPDF = useCallback(
		async (orderData, awbData) => {
			if (!orderData || !awbData) {
				if (window.ReactNativeWebView) {
					window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'error', description: 'noData' }));
				}
			}
			if (!orderData) return;
			if (!awbData) return;

			// fill the array with successful ids to be used in the print status update
			await mergePDF(async (merger) => {
				const dataToPrint = orderData.map((item) => {
					const itemAWB = awbData?.items?.find((awb) => awb?.id === item?.ID);

					return {
						invoiceData: item,
						awbData: itemAWB
					};
				});

				const printAWBData = dataToPrint.filter((item) => item?.awbData?.errorCode !== 'printAWBNA');

				const awbQueue = [];
				for await (const item of printAWBData) {
					// Create Invoice PDF
					let saudiQRData;

					if (window.ReactNativeWebView) {
						if (!clientData) {
							window.ReactNativeWebView.postMessage(
								JSON.stringify({ type: 'error', description: 'clientDataNotFound' })
							);
							return AppLogger.error('generateQRDataArr : clientData not found external print');
						}
						saudiQRData = generateBase64String(item.invoiceData, clientData);
					} else {
						saudiQRData = generateBase64String(item.invoiceData, {
							ClientName: companySettings?.ClientName ?? '',
							VATNumber: companySettings?.VATNumber ?? '000000000000000'
						});
					}

					const qrCode = await generateQrCode(saudiQRData, 200);

					awbQueue.push({
						propJSON: invoicePrintSettings,
						companyLogo: item.invoiceData?.CompanyLogo ?? null,
						fieldName: 'invoicePrintPropJSON',
						qrCode,
						...item.invoiceData,
						key: item?.invoiceData?.orderID
					});

					if (item.awbData?.filePath) {
						awbQueue.push({
							filePath: item.awbData.filePath
						});
					} else {
						if (window.ReactNativeWebView) {
							window.ReactNativeWebView.postMessage(
								JSON.stringify({ type: 'error', description: 'noFilePath' })
							);
						}
						throw new Error('No file path or error code!');
					}
				}

				const awbFiles = await renderPDFChunks(awbQueue, 25, renderAwbPDF);
				for (const pdfBlob of awbFiles) {
					if (pdfBlob) {
						await merger.add(pdfBlob);
					}
				}

				const printAWBNAData = dataToPrint.filter((item) => item?.awbData?.errorCode === 'printAWBNA');
				const result = await getPrintAWBNAData({ items: printAWBNAData });
				const pdfBlobs = await renderPDFChunks(result);
				for (const pdfBlob of pdfBlobs) {
					await merger.add(pdfBlob);
				}

				if (window.ReactNativeWebView) {
					const buffer = await merger.saveAsBuffer();
					const u8 = new Uint8Array(buffer);
					const b64 = Buffer.from(u8).toString('base64');
					window.ReactNativeWebView.postMessage(b64);
				} else {
					const mergedPdf = await merger.saveAsBlob();
					return mergedPdf;
				}
			});
		},
		[awbPrintSettings, invoicePrintSettings]
	);

	const handleLanguageChange = useCallback(
		async (value) => {
			dispatch(setPrintLocale(value)).then(() => {
				dispatch(setIsUserSelectedLocale(true));
				dispatch(
					setPrintSettings({
						awbPrintPropJSON: { ...awbPrintSettings, language: value },
						invoicePrintPropJSON: { ...invoicePrintSettings, language: value }
					})
				);
			});
		},
		[invoicePrintSettings, awbPrintSettings]
	);

	const fetchOrders = async (idSet) => {
		if (!idSet) return;
		await getInvoicePropJSON();
		await getAWBPropJSON();
		const awbData = await fetchAWBData(idSet);
		setAWBData(awbData);
		if (!awbData) {
			if (window.ReactNativeWebView) {
				window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'error', description: 'printAWBNA' }));
			}
			setError(true);
			showError();
			return;
		}

		const orderData = await fetchOrderPrintData(idSet, false, null, true);
		setInternationalOrders(
			() => orderData?.filter((order) => order.isInternationalShipment).map((order) => order) ?? []
		);
		setOrderData(orderData);
		if (!orderData) {
			if (window.ReactNativeWebView) {
				window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'error', description: 'printAWBNA' }));
			}
			setError(true);
			showError();
		}
	};

	const handleModalClose = useCallback(
		async (orderData) => {
			const internationalOrders = orderData
				.filter((order) => order.isInternationalShipment)
				.map((order) => Number(order.ID));

			// update print status
			if (successfulIDs?.length > 0) {
				const uniqueIDs = [...new Set(successfulIDs)];
				await updatePrintStatus(uniqueIDs);
			}

			modal.resolve({
				internationalOrders
			});
		},
		[successfulIDs]
	);

	// Effects

	useEffect(() => {
		if (!ids) return;
		fetchOrders(ids);
	}, [ids]);

	useEffect(() => {
		setAWBPrintSettings((prevState) => ({
			...prevState,
			...(printLocale && { language: printLocale })
		}));
		setInvoicePrintSettings((prevState) => ({
			...prevState,
			...(printLocale && { language: printLocale })
		}));
	}, [printLocale]);

	useEffect(() => {
		if (!invoicePrintSettings || !awbPrintSettings) return;
		if (!awbData || !orderData) return;

		if (invoicePrintSettings.templateName === 'template2' || invoicePrintSettings.templateName === 'template5') {
			fetchLanguage([invoicePrintSettings.language, 'arabicValue']).then(() => createPDF(orderData, awbData));
		} else {
			fetchLanguage(invoicePrintSettings.language).then(() => createPDF(orderData, awbData));
		}
	}, [awbData, orderData, invoicePrintSettings, awbPrintSettings]);

	useEffect(() => {
		if (error) {
			modal.remove();
			if (window.ReactNativeWebView) {
				window.ReactNativeWebView.postMessage(
					JSON.stringify({ type: 'error', description: error.response?.data?.code })
				);
			}
			showError();
		}
	}, [error]);

	return (
		<>
			{!isLoading ? (
				<Modal
					closeButton
					onClose={() => handleModalClose(internationalOrders)}
					maxWidth="x-large"
					fullwidth
					className="modal-printOrdersWithAWB">
					<ModalTitle>
						<Typography className="me-3" variant="p1">
							orderDetailsWithAWB
						</Typography>
						<FormItem colSpan={defaultColSpan}>
							<EnumSelect
								valueRemovable={false}
								data={langs}
								value={printLocale}
								setValue={(value) => {
									handleLanguageChange(value);
								}}
								searchable={false}
							/>
						</FormItem>
						{mobile && pdfFile && (
							<FormItem colSpan={defaultColSpan}>
								<Button
									fullWidth={true}
									onClick={() => handlePDFDownload(pdfFile, `Invoice-AWB-${ids.join('-')}`)}>
									{t('download')}
								</Button>
							</FormItem>
						)}
					</ModalTitle>
					{!isMobileDevice && (
						<ModalContent
							style={{
								height: '80dvh'
							}}>
							{pdfFile ? (
								<div
									style={{ height: '100%' }}
									dangerouslySetInnerHTML={{
										__html: `<iframe src='${pdfFile}' width='100%' height='100%' />`
									}}
								/>
							) : (
								<Loading />
							)}
						</ModalContent>
					)}
				</Modal>
			) : (
				<Loading />
			)}
		</>
	);
});

export default PrintOrdersWithAWB;
